/**
 * CampaignElementsList Component (Google Ads)
 *
 * Displays a list of campaign elements (asset groups for PMax, ad groups
 * with nested ads for other campaign types) with kebab menus for
 * pause/enable/remove actions.
 *
 * Brain (local optimistic state + pendingChangesStore subscription +
 * discard callback wiring + handleAction + isHidden/getEffectiveStatus)
 * lives in `useOptimisticEntityUpdates`. Rendering of the two-tier
 * ad-group/ads tree is delegated to `<EntityTreeList>` (shared with
 * `MetaCampaignElementsList`). PMax's single-tier asset-group list also
 * flows through `<EntityTreeList>` with `children: undefined`.
 */

import { useMemo, useCallback } from 'react';
import type { Campaign, Ad } from '@/types/campaign';
import type { KebabMenuOption } from '@/components/ui/KebabMenu';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { EntityStatusAction } from '@/services/api/campaigns';
import type { RDACacheEntry } from '@/hooks/useRDACache';
import type { DemandGenCacheEntry } from '@/hooks/useDemandGenCache';
import { getStatusBadgeClasses } from '@/utils/statusStyles';
import { formatEnumPretty } from '@/utils/formatEnum';
import { useOptimisticEntityUpdates } from '@/hooks/useOptimisticEntityUpdates';
import {
  EntityTreeList,
  type GetMenuOptionsParams,
  type NormalizedTreeChild,
  type NormalizedTreeParent,
} from './EntityTreeList';

interface CampaignElementsListProps {
  campaign: Campaign;
  isPerformanceMax: boolean;
  onElementUpdated: () => void;
  onEntityRemoved?: (entityId: string) => void;
  rdaCache?: Record<string, RDACacheEntry>;
  isLoadingRDAs?: boolean;
  demandGenCache?: Record<string, DemandGenCacheEntry>;
  isLoadingDemandGenAds?: boolean;
}

type GoogleAction = EntityStatusAction; // 'pause' | 'enable' | 'remove'
type GoogleEntityType = 'ad_group' | 'asset_group' | 'ad';

const GOOGLE_VALID_ACTIONS: readonly GoogleAction[] = ['pause', 'enable', 'remove'];
const GOOGLE_HIDDEN_STATUSES: readonly string[] = ['REMOVED'];

function formatGoogleToastSubject(type: GoogleEntityType): string {
  return type.replace('_', ' ');
}

// Get ad type label
function getAdTypeLabel(adType: string): string {
  if (adType === 'RESPONSIVE_SEARCH_AD') return 'RSA';
  if (adType === 'RESPONSIVE_DISPLAY_AD') return 'RDA';
  if (adType === 'DEMAND_GEN_MULTI_ASSET_AD') return 'DG Multi-Asset';
  if (adType === 'DEMAND_GEN_CAROUSEL_AD') return 'DG Carousel';
  if (adType === 'DEMAND_GEN_VIDEO_RESPONSIVE_AD') return 'DG Video';
  return adType.replace(/_/g, ' ');
}

export function CampaignElementsList({
  campaign,
  isPerformanceMax,
  onElementUpdated,
  onEntityRemoved,
  rdaCache = {},
  isLoadingRDAs = false,
  demandGenCache = {},
  isLoadingDemandGenAds = false,
}: CampaignElementsListProps) {
  const {
    hasPendingChange,
    getPendingAction,
    getEffectiveStatus,
    isHidden,
    handleAction,
    pendingAction,
    setPendingAction,
    confirmPendingAction,
  } = useOptimisticEntityUpdates<GoogleAction, GoogleEntityType>({
    campaignId: campaign.campaign_id,
    platform: 'google_ads',
    validActions: GOOGLE_VALID_ACTIONS,
    terminalAction: 'remove',
    activeStatus: 'ENABLED',
    pausedStatus: 'PAUSED',
    hiddenStatuses: GOOGLE_HIDDEN_STATUSES,
    discardCallbackKeyPrefix: 'campaign-elements',
    onElementUpdated,
    onTerminalAction: onEntityRemoved,
    formatToastSubject: formatGoogleToastSubject,
  });

  // Ad display name: RDA → cached headlines preview; Demand Gen →
  // `<ad_name> | <headline>`; RSA/others → first three headlines joined.
  const getAdDisplayName = useCallback(
    (ad: Ad): string => {
      if (ad.ad_type === 'RESPONSIVE_DISPLAY_AD') {
        const cached = rdaCache[ad.ad_id];
        if (cached) {
          const headlines = cached.headlines || [];
          const allHeadlines = cached.long_headline
            ? [...headlines.slice(0, 2), cached.long_headline]
            : headlines.slice(0, 3);
          if (allHeadlines.length > 0) {
            return allHeadlines.join(' | ');
          }
        }
        if (isLoadingRDAs) {
          return ad.ad_name || 'Loading...';
        }
        return ad.ad_name || `Ad ${ad.ad_id}`;
      }

      if (ad.ad_type?.startsWith('DEMAND_GEN')) {
        const cached = demandGenCache[ad.ad_id];
        const adName = cached?.ad_name || ad.ad_name || '';
        let headline = '';

        const headlines: Array<string | { text: string; pinned_field?: string }> =
          cached?.headlines || ad.headlines || [];
        if (headlines.length > 0) {
          const first = headlines[0];
          headline = typeof first === 'string' ? first : first.text;
        }

        if (isLoadingDemandGenAds && !cached) {
          return adName || 'Loading...';
        }
        if (adName && headline) return `${adName} | ${headline}`;
        if (adName) return adName;
        if (headline) return headline;
        const businessName = cached?.business_name || ad.business_name;
        if (businessName) return businessName;
        return `Ad ${ad.ad_id}`;
      }

      const headlines = ad.headlines || [];
      if (headlines.length > 0) {
        const headlineTexts = headlines
          .slice(0, 3)
          .map((h) => (typeof h === 'string' ? h : h.text));
        return headlineTexts.join(' | ');
      }
      return ad.ad_name || `Ad ${ad.ad_id}`;
    },
    [rdaCache, isLoadingRDAs, demandGenCache, isLoadingDemandGenAds],
  );

  // Normalize Google ad groups + ads into NormalizedTreeParent[].
  const normalizedAdGroups = useMemo<NormalizedTreeParent[]>(() => {
    if (!campaign.ads_group_list) return [];
    return campaign.ads_group_list
      .filter((ag) => {
        const agId = ag.ad_group_id || ag.ads_group_id || '';
        return !isHidden(agId, ag.status || 'ENABLED');
      })
      .map((ag) => {
        const agId = ag.ad_group_id || ag.ads_group_id || '';
        const agName = ag.ad_group_name || ag.ads_group_name || 'Unnamed';
        const status = ag.status || 'ENABLED';
        const children: NormalizedTreeChild[] = (ag.ads_list || ag.ads || [])
          .filter((ad) => !isHidden(ad.ad_id, ad.status || 'ENABLED'))
          .map((ad) => ({
            id: ad.ad_id,
            name: getAdDisplayName(ad),
            status: ad.status || 'ENABLED',
            entityType: 'ad',
            parentId: agId,
            typeLabel: getAdTypeLabel(ad.ad_type),
          }));
        return {
          id: agId,
          name: agName,
          status,
          entityType: 'ad_group',
          children,
        };
      });
  }, [campaign.ads_group_list, isHidden, getAdDisplayName]);

  // Normalize Google asset groups (PMax). Single-tier — no children.
  const normalizedAssetGroups = useMemo<NormalizedTreeParent[]>(() => {
    if (!campaign.asset_group_list) return [];
    return campaign.asset_group_list
      .filter((ag) => !isHidden(ag.asset_group_id, ag.status || ag.asset_group_status || 'ENABLED'))
      .map((ag) => ({
        id: ag.asset_group_id,
        name: ag.asset_group_name,
        status: ag.status || ag.asset_group_status || 'ENABLED',
        entityType: 'asset_group',
      }));
  }, [campaign.asset_group_list, isHidden]);

  // Status badge — Google vocab.
  const StatusBadge = useCallback(
    ({ status, entityId }: { status: string; entityId: string }) => {
      const effectiveStatus = getEffectiveStatus(entityId, status);
      const isPending = hasPendingChange(entityId);
      const pendingActionType = getPendingAction(entityId);

      let displayStatus = effectiveStatus;
      let displayLabel = formatEnumPretty(effectiveStatus);

      if (isPending && pendingActionType) {
        displayStatus =
          pendingActionType === 'enable'
            ? 'ENABLING'
            : pendingActionType === 'pause'
              ? 'PAUSING'
              : 'REMOVING';
        displayLabel =
          pendingActionType === 'enable'
            ? 'Enabling...'
            : pendingActionType === 'pause'
              ? 'Pausing...'
              : 'Removing...';
      }

      return (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClasses(displayStatus)}`}
        >
          {displayLabel}
        </span>
      );
    },
    [getEffectiveStatus, hasPendingChange, getPendingAction],
  );

  const getMenuOptions = useCallback(
    ({ type, entityId, entityName, status, parentId }: GetMenuOptionsParams): KebabMenuOption[] => {
      const googleType = type as GoogleEntityType;
      const effectiveStatus = getEffectiveStatus(entityId, status);
      const isPending = hasPendingChange(entityId);
      const options: KebabMenuOption[] = [];

      if (effectiveStatus === 'ENABLED') {
        options.push({
          label: 'Pause',
          onClick: () => handleAction(googleType, entityId, 'pause', entityName, parentId),
          disabled: isPending,
        });
      } else if (effectiveStatus === 'PAUSED') {
        options.push({
          label: 'Enable',
          onClick: () => handleAction(googleType, entityId, 'enable', entityName, parentId),
          variant: 'success',
          disabled: isPending,
        });
      }

      options.push({
        label: 'Remove',
        onClick: () =>
          setPendingAction({
            type: googleType,
            action: 'remove',
            entityId,
            entityName,
            adGroupId: parentId,
          }),
        variant: 'danger',
        disabled: isPending,
      });

      return options;
    },
    [getEffectiveStatus, hasPendingChange, handleAction, setPendingAction],
  );

  // Empty state
  if (isPerformanceMax && normalizedAssetGroups.length === 0) return null;
  if (!isPerformanceMax && normalizedAdGroups.length === 0) return null;

  const parents = isPerformanceMax ? normalizedAssetGroups : normalizedAdGroups;

  return (
    <>
      <div className="mt-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          {isPerformanceMax ? 'Asset Groups' : 'Ad Groups & Ads'}
        </h4>

        <EntityTreeList
          parents={parents}
          StatusBadge={StatusBadge}
          getMenuOptions={getMenuOptions}
        />
      </div>

      <ConfirmDialog
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmPendingAction}
        title={`Remove ${pendingAction?.type === 'ad_group' ? 'Ad Group' : pendingAction?.type === 'asset_group' ? 'Asset Group' : 'Ad'}?`}
        message={`Are you sure you want to remove "${pendingAction?.entityName}"? This will queue the change - click Save Changes to apply. Once saved, this cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
}

export default CampaignElementsList;
